import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { translate } from "../../../utils/translations/translate";
import {
  BILLABLE_OPTIONS,
  PAY_CODE_OPTIONS,
  TOAST_TIMER,
  MAX_FILE_SIZE_IN_MB,
} from "../../../utils/constants";
export const useTimeSheetManager = (
  weekDaysArr,
  timesheetId,
  setToastInfo,
  onDeleteAttachment
) => {
  const newTaskDetails = useMemo(() => {
    return {
      projectTaskId: "",
      projectTaskName: "",
      projectTask: "",
      payCode: PAY_CODE_OPTIONS[0].value,
      billableType: BILLABLE_OPTIONS[0].value,
      hours: weekDaysArr.map((week) => {
        return {
          day: new Date(week.fullDate),
          hours: 0,
          comparator: week.comparator,
        };
      }),
    };
  }, [weekDaysArr]);

  const [taskRows, setTaskRows] = useState([newTaskDetails]);
  const [alertMessage, setAlertMessage] = useState("");
  const commentBoxRef = useRef(null);
  const [attachments, setAttachments] = useState([{ file: '' }]);

  const handleAddAttachment = () => {
    setAttachments((prev) => [...prev, { file: '' }]);
  };

  const handleFileChange = (index, file) => {
    const newAttachments = [...attachments];
    newAttachments[index].file = file[0];
    setAttachments(newAttachments);
  };

  const handleDeleteAttachment = async (fileName) => {
    try {
      const response = await onDeleteAttachment(timesheetId, fileName);
      return response ? true : false;
    } catch (e) {
      return false;
    }
  };

  const handleOtherAttachmentsRemove = (baseIndx) => {
    const removeOtherAttachment = () => {
      setAttachments((prev) => {
        const updated = [...prev];
        updated[baseIndx] = { ...updated[baseIndx], file: '' };
        return updated;
      });
    };

    if (
      attachments[baseIndx].file &&
      typeof attachments[baseIndx].file.orignalFile === "string"
    ) {
      handleDeleteAttachment(attachments[baseIndx].file.orignalFile).then(
        (res) => {
          if (res === true) {
            removeOtherAttachment();
          }
        }
      );
    } else {
      removeOtherAttachment();
    }
  };

  const handleMaxLimitExceeded = useCallback(() => {
    setToastInfo({
      message: translate("labels.uploadMaxNotification").replace(
        "$maxSize",
        MAX_FILE_SIZE_IN_MB
      ),
      type: "error",
      autoExpire: true,
    });
  }, [setToastInfo]);

  const calculateTotal = useCallback((array, accessor = (item) => item) => {
    return array.reduce((acc, cur) => {
      const value = accessor(cur);
      return acc + (value !== null ? value : 0);
    }, 0);
  }, []);

  const addNewTaskRow = () => {
    setTaskRows((prev) => [...prev, newTaskDetails]);
  };

  const deleteTaskRow = (rowIndex) => {
    const prevTasks = JSON.parse(JSON.stringify([...taskRows]));
    prevTasks.splice(rowIndex, 1);
    const newTasks = [...prevTasks];
    setTaskRows(newTasks.length > 0 ? newTasks : [newTaskDetails]);
  };

  const updateTimeEntries = (rowIndex, date, timeVal, inputRef) => {
    const currentTaskRows = JSON.parse(JSON.stringify([...taskRows]));
    const parsedInput = parseInt(timeVal, 10);
    const newTimeValue =
      isNaN(parsedInput) || parsedInput < 0 ? 0 : parsedInput;
    const colTotal = currentTaskRows.reduce((total, row) => {
      const entry = row.hours.find((hr) => hr.comparator === date.comparator);
      return total + (entry ? entry.hours || 0 : 0);
    }, 0);

    const rowToBeUpdated = currentTaskRows[rowIndex];
    const timeEntryToBeUpdatedIdx = rowToBeUpdated.hours.findIndex(
      (hr) => hr.comparator === date.comparator
    );

    if (timeEntryToBeUpdatedIdx > -1) {
      const currentCellValue =
        rowToBeUpdated.hours[timeEntryToBeUpdatedIdx].hours || 0;
      const newColTotal = colTotal - currentCellValue + newTimeValue;

      if (newColTotal > 24) {
        const maxAllowedValue = 24 - (colTotal - currentCellValue);
        rowToBeUpdated.hours[timeEntryToBeUpdatedIdx].hours = maxAllowedValue;
        const adjustedTimeEntryMessage =
          translate("labels.maxHoursExceeded") + " " + date.comparator;
        setAlertMessage(adjustedTimeEntryMessage);
        setTimeout(() => {
          setAlertMessage("");
        }, TOAST_TIMER);
      } else {
        let inValue = !newTimeValue && newTimeValue !== 0 ? null : newTimeValue;
        if (timeVal === "") {
          inValue = null;
        }
        rowToBeUpdated.hours[timeEntryToBeUpdatedIdx].hours = inValue;
      }
    }
    setTaskRows(currentTaskRows);
  };

  const reInitializeTaskRows = useCallback(
    (data) => {
      const taskRowData = data.length > 0 ? data : [newTaskDetails];
      setTaskRows(taskRowData);
    },
    [newTaskDetails]
  );

  const getCurrentHrValue = useCallback((row, date) => {
    if (row) {
      const curValue = row.hours.find(
        (hr) => hr.comparator === date.comparator
      ) ?.hours;
      if (curValue === null) return null;
      return parseInt(curValue || 0, 10);
    }
    return null;
  }, []);

  const updateProjectTaskEntries = (rowIndex, field, option) => {
    const currentTaskRows = JSON.parse(JSON.stringify([...taskRows]));
    const rowToBeUpdated = currentTaskRows[rowIndex];
    const validFields = ["projectTask", "billableType", "payCode"];
    if (!rowToBeUpdated || !validFields.includes(field)) return;

    if (field === "projectTask") {
      rowToBeUpdated["projectTaskId"] = option.value;
      rowToBeUpdated["projectTask"] = option.value;
      rowToBeUpdated["projectTaskName"] = option.label;
    } else {
      rowToBeUpdated[field] = option.value;
    }

    setTaskRows(currentTaskRows);
  };

  const formattedWeekLabels = useMemo(
    () => [...weekDaysArr, { dateLabel: "", dayLabel: "Total" }],
    [weekDaysArr]
  );

  const getRowTotalHours = useCallback(
    (row) => calculateTotal(row.hours, (hr) => hr.hours),
    [calculateTotal]
  );

  const rowMappingHours = useMemo(() => {
    return taskRows.map((row) => {
      return { ...row, rowTotal: getRowTotalHours(row) };
    });
  }, [taskRows, getRowTotalHours]);

  const getColTotal = useCallback((rows, week) => {
    const currentTaskRows = JSON.parse(JSON.stringify([...rows]));
    const currentWeekTotal = currentTaskRows
      .flatMap((row) => {
        return row.hours.filter((hr) => hr.comparator === week.comparator);
      })
      .reduce((acc, cur) => {
        if (cur.hours === null) return acc;
        acc = acc + cur.hours;
        return acc;
      }, 0);
    return currentWeekTotal;
  }, []);

  const totalInputtedHours = useMemo(
    () =>
      calculateTotal(
        taskRows.flatMap((row) => row.hours),
        (hr) => hr.hours
      ),
    [taskRows, calculateTotal]
  );

  const getProjectTaskRowValues = (rowIndex) => {
    const row = taskRows[rowIndex];
    const { projectTask, payCode, billableType } = row || newTaskDetails;
    return { projectTask, payCode, billableType };
  };

  const hrMappingByDate = useMemo(() => {
    const mappingsObj = formattedWeekLabels
      .slice(0, formattedWeekLabels.length - 1)
      .reduce((acc, week) => {
        acc[week.comparator] = {
          total: getColTotal(taskRows, week),
          rows: taskRows.map((row) => getCurrentHrValue(row, week)),
        };
        return acc;
      }, {});
    return mappingsObj;
  }, [taskRows, formattedWeekLabels, getColTotal, getCurrentHrValue]);

  const copyWithEntries = useCallback(
    (entries) => {
      if (entries.length === 0) {
        setAlertMessage(translate("labels.noEntriesFound"));
      } else {
        const currentDisabledEntries = formattedWeekLabels
          .filter((label) => label.isFutureDate)
          .map((label) => label.comparator);

        const adjustedEntries = entries.map((entry) => {
          const adjustedHours = entry.hours.map((hr) => {
            let rHours = { ...hr };
            if (currentDisabledEntries.includes(hr.comparator)) {
              rHours.hours = 0;
            }
            return rHours;
          });
          return {
            ...entry,
            hours: adjustedHours,
          };
        });
        setTaskRows(adjustedEntries);
        setAlertMessage(translate("labels.copiedPreviousEntries"));
      }

      setTimeout(() => setAlertMessage(""), TOAST_TIMER);
    },
    [formattedWeekLabels]
  );

  return {
    addNewTaskRow,
    deleteTaskRow,
    updateTimeEntries,
    updateProjectTaskEntries,
    formattedWeekLabels,
    rowMappingHours,
    totalInputtedHours,
    getProjectTaskRowValues,
    hrMappingByDate,
    taskRows,
    alertMessage,
    commentBoxRef,
    copyWithEntries,
    reInitializeTaskRows,
    handleAddAttachment,
    handleFileChange,
    handleDeleteAttachment,
    handleOtherAttachmentsRemove,
    handleMaxLimitExceeded,
    attachments,
    setAttachments,
  };
};
